import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  GraduationCap, 
  ArrowLeft, 
  CheckCircle
} from 'lucide-react';
import { db } from '../../database/supabase';
import { useAuth } from '../../contexts/AuthContext';

const HedExitInterviewForm = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const onSubmit = async (data) => {
    if (!agreedToPrivacy) {
      toast.error('Please agree to the data privacy consent');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data: formTemplate, error: formError } = await db.getFormByType('hed_exit_interview');
      
      if (formError) {
        throw new Error('Could not load form template');
      }

      const submissionData = {
        form_id: formTemplate.id,
        user_id: user?.id || null,
        data: { ...data, privacyConsent: agreedToPrivacy },
        status: 'submitted',
        submitted_at: new Date().toISOString()
      };

      const { error: submitError } = await db.createSubmission(submissionData);
      
      if (submitError) {
        throw submitError;
      }

      toast.success('HED Exit interview submitted successfully!');
      navigate('/forms');
      
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        <div className="mb-8">
          <button
            onClick={() => navigate('/forms')}
            className="flex items-center text-blue-800 hover:text-red-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Forms
          </button>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-blue-800" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              HED Exit Interview Form
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Please complete this exit interview form to help us improve our services and programs.
            </p>
          </div>
        </div>

        <div className="form-container">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            
            {/* DATA PRIVACY CONSENT */}
            <div className="form-section">
              <h2 className="form-section-title text-blue-800 text-xl font-bold mb-4">
                DATA PRIVACY CONSENT AND AGREEMENT
              </h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <p className="text-sm text-gray-700 mb-4">
                  By proceeding with this form, you acknowledge that the information you provide will be collected, 
                  processed, and stored in accordance with our data privacy policy. Your personal information will 
                  be used solely for institutional improvement and research purposes.
                </p>
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={agreedToPrivacy}
                    onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                    className="mt-1"
                    required
                  />
                  <span className="text-sm text-gray-700">
                    I agree to the data privacy terms and consent to the collection and processing of my personal information.
                  </span>
                </label>
              </div>
            </div>

            {/* AGREEMENT */}
            <div className="form-section">
              <label className="form-label">AGREEMENT *</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="agree"
                    {...register('agreement', { required: 'Agreement is required' })}
                    className="mr-3"
                  />
                  I agree to participate in this exit interview
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="disagree"
                    {...register('agreement')}
                    className="mr-3"
                  />
                  I do not agree to participate
                </label>
              </div>
              {errors.agreement && (
                <p className="form-error">{errors.agreement.message}</p>
              )}
            </div>

            {/* A. PERSONAL INFORMATION */}
            <div className="border-t-4 border-blue-800 pt-8">
              <h2 className="form-section-title text-blue-800 text-xl font-bold mb-6">
                A. PERSONAL INFORMATION
              </h2>
              
              <div className="form-grid">
                <div>
                  <label className="form-label">FULL NAME *</label>
                  <input
                    type="text"
                    className="form-input"
                    {...register('fullName', { required: 'Full name is required' })}
                  />
                  {errors.fullName && <p className="form-error">{errors.fullName.message}</p>}
                </div>

                <div>
                  <label className="form-label">PROGRAM *</label>
                  <select className="form-input" {...register('program', { required: 'Program is required' })}>
                    <option value="">Select your program</option>
                    <option value="STEM">STEM</option>
                    <option value="ABM">ABM</option>
                    <option value="HUMSS">HUMSS</option>
                    <option value="GAS">GAS</option>
                    <option value="TVL">TVL</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.program && <p className="form-error">{errors.program.message}</p>}
                </div>

                <div>
                  <label className="form-label">MAJOR</label>
                  <input type="text" className="form-input" {...register('major')} />
                </div>

                <div>
                  <label className="form-label">AWARD/S RECEIVED</label>
                  <textarea className="form-input" rows="3" {...register('awards')} />
                </div>

                <div>
                  <label className="form-label">DATE FILLED OUT *</label>
                  <input
                    type="date"
                    className="form-input"
                    {...register('dateFilled', { required: 'Date is required' })}
                    defaultValue={new Date().toISOString().split('T')[0]}
                  />
                  {errors.dateFilled && <p className="form-error">{errors.dateFilled.message}</p>}
                </div>

                <div>
                  <label className="form-label">DATE OF BIRTH *</label>
                  <input
                    type="date"
                    className="form-input"
                    {...register('dateOfBirth', { required: 'Date of birth is required' })}
                  />
                  {errors.dateOfBirth && <p className="form-error">{errors.dateOfBirth.message}</p>}
                </div>

                <div>
                  <label className="form-label">AGE (AS OF FILLING OUT FORM)</label>
                  <input type="number" className="form-input" {...register('age')} />
                </div>

                <div>
                  <label className="form-label">GENDER *</label>
                  <div className="space-y-2">
                    {['Male', 'Female', 'Other'].map((gender) => (
                      <label key={gender} className="flex items-center">
                        <input
                          type="radio"
                          value={gender}
                          {...register('gender', { required: 'Gender is required' })}
                          className="mr-3"
                        />
                        {gender}
                      </label>
                    ))}
                  </div>
                  {errors.gender && <p className="form-error">{errors.gender.message}</p>}
                </div>

                <div>
                  <label className="form-label">HOME ADDRESS</label>
                  <textarea className="form-input" rows="2" {...register('homeAddress')} />
                </div>

                <div>
                  <label className="form-label">EMAIL ADDRESS</label>
                  <input type="email" className="form-input" {...register('emailAddress')} />
                </div>

                <div>
                  <label className="form-label">CONTACT NUMBER</label>
                  <input type="tel" className="form-input" {...register('contactNumber')} />
                </div>
              </div>
            </div>

            {/* B. EXIT INTERVIEW QUESTIONS */}
            <div className="border-t-4 border-blue-800 pt-8">
              <h2 className="form-section-title text-blue-800 text-xl font-bold mb-6">
                B. EXIT INTERVIEW QUESTIONS
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="form-label">1. What are your plans after graduation?</label>
                  <textarea className="form-input" rows="4" {...register('plansAfterGraduation')} />
                </div>

                <div>
                  <label className="form-label">2a. Do you feel ready to compete with other graduates in applying for a job? *</label>
                  <div className="space-y-2">
                    {['Yes', 'No', 'Somewhat'].map((option) => (
                      <label key={option} className="flex items-center">
                        <input
                          type="radio"
                          value={option}
                          {...register('readyToCompete', { required: 'Please select an option' })}
                          className="mr-3"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                  {errors.readyToCompete && <p className="form-error">{errors.readyToCompete.message}</p>}
                </div>

                <div>
                  <label className="form-label">2b. Following up from the previous question: why or why not?</label>
                  <textarea className="form-input" rows="4" {...register('whyReadyOrNot')} />
                </div>

                <div>
                  <label className="form-label">3. Tell us about the best experience/s you have had at SLMCSI.</label>
                  <textarea className="form-input" rows="4" {...register('bestExperience')} />
                </div>

                <div>
                  <label className="form-label">4. What has been your most challenging experience at SLMCSI?</label>
                  <textarea className="form-input" rows="4" {...register('challengingExperience')} />
                </div>

                <div>
                  <label className="form-label">5. How did SLMCSI prepare you for your future plans?</label>
                  <textarea className="form-input" rows="4" {...register('howPrepared')} />
                </div>

                <div>
                  <label className="form-label">6. What do you wish to see improved at SLMCSI (e.g. curriculum, activities, services, et cetera)?</label>
                  <textarea className="form-input" rows="4" {...register('improvementSuggestions')} />
                </div>

                <div>
                  <label className="form-label">7. How do you see yourself five (5) years from now?</label>
                  <textarea className="form-input" rows="4" {...register('fiveYearVision')} />
                </div>
              </div>
            </div>

            {/* C. SERVICES SATISFACTION SURVEY */}
            <div className="border-t-4 border-blue-800 pt-8">
              <h2 className="form-section-title text-blue-800 text-xl font-bold mb-6">
                C. SERVICES SATISFACTION SURVEY
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Rate each service on a scale of 1-5 (1 = Poor, 2 = Fair, 3 = Good, 4 = Very Good, 5 = Excellent)
              </p>
              
              <div className="space-y-6">
                {[
                  "Dean's Office",
                  "Office of the Student Affairs Coordinator (SAC)",
                  "Registrar's Office",
                  "Accounting Office",
                  "Library / Learning Resource Center (LRC)",
                  "Office of Guidance and Counseling Services",
                  "School Canteen",
                  "Comfort Rooms",
                  "Health Clinic",
                  "Security",
                  "School Maintenance",
                  "SLMCSI Overall"
                ].map((service, index) => (
                  <div key={index}>
                    <label className="form-label">{service}</label>
                    <div className="flex space-x-6">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <label key={rating} className="flex items-center">
                          <input
                            type="radio"
                            value={rating}
                            {...register(`service_${index}`, { required: `Please rate ${service}` })}
                            className="mr-2"
                          />
                          <span className="text-sm">{rating}</span>
                        </label>
                      ))}
                    </div>
                    {errors[`service_${index}`] && (
                      <p className="form-error">{errors[`service_${index}`].message}</p>
                    )}
                  </div>
                ))}

                <div>
                  <label className="form-label">Other remarks/insights/comments/suggestions:</label>
                  <textarea className="form-input" rows="4" {...register('additionalComments')} />
                </div>
              </div>
            </div>

            <div className="flex justify-center pt-6">
              <button
                type="submit"
                disabled={isSubmitting || !agreedToPrivacy}
                className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner w-4 h-4 mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit HED Exit Interview
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HedExitInterviewForm;